import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  inject,
  linkedSignal,
  signal,
} from "@angular/core";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { DatePipe, DecimalPipe } from "@angular/common";
import { AgGridAngular } from "@ag-grid-community/angular";
import { DspBlacklist, DspBlacklistTypeEnum, DspStats, SiteType } from "@front/m19-api-client";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  AccountSelectionService,
  AuthService,
  DspService,
  StatsService,
  UserSelectionService,
} from "@front/m19-services";
import { DspGridColumnsService, dspGridOptions } from "@m19-board/dsp/dsp-stats/dsp-grid-columns.service";
import { combineLatest, concat, EMPTY, finalize, map, Subject, switchMap, take, tap } from "rxjs";
import { DSP_CLICKS, DSP_COST, DSP_IMPRESSIONS, DSP_ROAS } from "@m19-board/models/DspMetrics";
import { startWith } from "rxjs/operators";
import { DspInventoryStats } from "@m19-board/dsp/dsp-stats/dsp-strategy-stats-grid.component";
import {
  ColDef,
  GetRowIdParams,
  GridOptions,
  ICellRendererParams,
  ValueFormatterParams,
} from "@ag-grid-community/core";
import { actionColumnProperties } from "@m19-board/grid-config/grid-columns";
import {
  ActionButton,
  ActionButtonsComponent,
  IBadgeComponent,
  IButtonDirective,
  ModalService,
  SpinnerComponent,
} from "@front/m19-ui";
import { dspBlacklistKey, getDspBlacklistFromSite, isApp, sumDspStats } from "@front/m19-utils";
import { ActivatedRoute } from "@angular/router";
import { Dashboard360UsavedChangesModalComponent } from "@m19-board/360-common/base-360-dashboard-view/dashboard360-usaved-changes-modal/dashboard360-usaved-changes-modal.component";

const BLACKLIST_METRIC_IDS = [DSP_ROAS.id, DSP_IMPRESSIONS.id, DSP_COST.id, DSP_CLICKS.id];

const BLACKLISTABLE_SITE_TYPES = [SiteType.APD, SiteType.TPE];

const MAX_BLACKLIST_ENTRIES = 100;

const REACTIVATE_COLUMN_WIDTH = 130;
const BLOCK_COLUMN_WIDTH = 80;

function isBlacklistableSupply(stat: DspStats): boolean {
  return !!stat.siteType && BLACKLISTABLE_SITE_TYPES.includes(stat.siteType) && stat.site != "Unallocated";
}

@Component({
  selector: "app-dsp-blacklist",
  imports: [
    TranslocoDirective,
    DatePipe,
    DecimalPipe,
    AgGridAngular,
    IButtonDirective,
    IBadgeComponent,
    SpinnerComponent,
  ],
  templateUrl: "./dsp-blacklist.component.html",
  host: { class: "block min-h-0 flex-1" },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspBlacklistComponent {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly statsService = inject(StatsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly dspColumns = inject(DspGridColumnsService);
  private readonly authService = inject(AuthService);
  private readonly dspService = inject(DspService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly modalService = inject(ModalService);

  @HostListener("window:beforeunload")
  onBeforeUnload() {
    return !this.hasChanged();
  }

  readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });
  private readonly currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });
  private readonly selectedDateRange = toSignal(this.userSelectionService.selectedDateRange$, { requireSync: true });
  readonly periodStart = computed(() => this.selectedDateRange().start.toJSDate());
  readonly periodEnd = computed(() => this.selectedDateRange().end.toJSDate());
  readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });
  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { initialValue: true });
  readonly isLoading = signal(false);
  readonly isInventoryLoading = signal(true);
  private readonly isBlacklistFetching = signal(true);
  readonly isBlacklistLoading = computed(() => this.isBlacklistFetching() || this.isLoading());

  readonly inventoryType = toSignal(
    this.activatedRoute.data.pipe(map((data) => <DspBlacklistTypeEnum>data["inventoryType"])),
    { requireSync: true },
  );

  private readonly dspInventoryStats = toSignal(
    combineLatest([
      this.accountSelectionService.singleAccountMarketplaceSelection$,
      this.userSelectionService.dateRange$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      tap(() => this.isInventoryLoading.set(true)),
      switchMap(([accountMarketplace, [start, end], currency]) =>
        this.statsService.getDspInventoryStats(
          accountMarketplace.accountId,
          accountMarketplace.marketplace,
          accountMarketplace.dspAdvertiserId!,
          currency,
          start,
          end,
        ),
      ),
      map((stats) => stats.filter(isBlacklistableSupply)),
      tap(() => this.isInventoryLoading.set(false)),
    ),
  );

  private readonly reloadBlacklist = new Subject<void>();

  private readonly blacklistSource = toSignal<DspBlacklist[]>(
    combineLatest([
      this.accountSelectionService.singleAccountMarketplaceSelection$,
      this.reloadBlacklist.pipe(startWith(undefined)),
    ]).pipe(
      tap(() => this.isBlacklistFetching.set(true)),
      switchMap(([am]) => this.dspService.getDspBlacklists(am)),
      tap(() => this.isBlacklistFetching.set(false)),
      startWith([]),
    ),
    { requireSync: true },
  );

  private readonly draftBlacklist = linkedSignal(() => this.blacklistSource());

  readonly blacklistedDomains = computed(() => this.draftBlacklist().filter((d) => d.type === this.inventoryType()));

  readonly maxBlacklistEntries = MAX_BLACKLIST_ENTRIES;

  private readonly isBlacklistFull = computed(() => this.blacklistedDomains().length >= MAX_BLACKLIST_ENTRIES);

  private readonly blacklistDiff = computed(() => {
    const sourceKeys = new Set(this.blacklistSource().map(dspBlacklistKey));
    const draftKeys = new Set(this.draftBlacklist().map(dspBlacklistKey));
    return {
      added: this.draftBlacklist().filter((d) => !sourceKeys.has(dspBlacklistKey(d))),
      removed: this.blacklistSource().filter((d) => !draftKeys.has(dspBlacklistKey(d))),
    };
  });

  readonly hasChanged = computed(
    () => this.blacklistDiff().added.length > 0 || this.blacklistDiff().removed.length > 0,
  );

  readonly inventoryRowData = computed<DspInventoryStats[]>(() => {
    const stats = this.filterDataByInventoryType(this.dspInventoryStats() ?? [], this.inventoryType());
    const inventoryStatsFn = (stat: DspStats): DspInventoryStats => stat;

    const blacklisted = new Set(this.draftBlacklist().map(dspBlacklistKey));

    return this.buildRowData<DspInventoryStats>(
      stats,
      (stat: DspStats) => stat.site! + "_" + stat.supplySource!,
      inventoryStatsFn,
    ).filter((row) => !blacklisted.has(dspBlacklistKey(getDspBlacklistFromSite(row.site!))));
  });

  readonly blacklistRowId = (params: GetRowIdParams<DspBlacklist>) => params.data.domainOrAppId;

  readonly blacklistGridColumnDefs = computed<ColDef<DspBlacklist>[]>(() => {
    const isReadOnly = this.isReadOnly();
    return [
      ...(this.inventoryType() === DspBlacklistTypeEnum.APP
        ? [
            this.blacklistTextColumn("dsp-blacklist.app_name", "description"),
            this.blacklistTextColumn("dsp-blacklist.app_id", "domainOrAppId"),
          ]
        : [this.blacklistTextColumn("dsp-stats.site", "domainOrAppId")]),
      {
        ...actionColumnProperties<DspBlacklist, string>(),
        resizable: false,
        width: REACTIVATE_COLUMN_WIDTH,
        minWidth: REACTIVATE_COLUMN_WIDTH,
        maxWidth: REACTIVATE_COLUMN_WIDTH,
        cellRendererSelector: (params: ICellRendererParams<DspBlacklist, string>) => {
          const btns: ActionButton[] = [
            {
              icon: "undo2",
              text: this.translocoService.translate("dsp-blacklist.reactivate"),
              disabled: isReadOnly,
              onClick: () => this.whitelist(params.data!),
            },
          ];

          return {
            component: ActionButtonsComponent,
            params: {
              actionButtons: btns,
            },
          };
        },
      },
    ];
  });

  readonly inventoryGridOptions: GridOptions<DspInventoryStats> = {
    ...dspGridOptions<DspInventoryStats>({ gridKey: "" }),
    sideBar: false,
    defaultColDef: { sortable: true, filter: true, flex: 1, minWidth: 100 },
  };

  readonly inventoryGridColumnDefs = computed<ColDef<DspInventoryStats>[]>(() => {
    const isReadOnly = this.isReadOnly();
    return [
      {
        headerValueGetter: () =>
          this.translocoService.translate(
            this.inventoryType() === DspBlacklistTypeEnum.APP ? "dsp-blacklist.app" : "dsp-stats.site",
          ),
        field: "site",
        floatingFilter: true,
        filter: "agTextColumnFilter",
        flex: 3,
      },
      ...this.dspColumns
        .metricsColumns<DspInventoryStats>()
        .filter((col) => BLACKLIST_METRIC_IDS.includes(col.colId!))
        .map((col): ColDef<DspInventoryStats> => (col.colId === DSP_COST.id ? this.costWithCentsColumn(col) : col)),
      this.dspColumns.currencyColumn<DspInventoryStats>(),
      {
        ...actionColumnProperties<DspInventoryStats, string>(),
        resizable: false,
        flex: 0,
        width: BLOCK_COLUMN_WIDTH,
        minWidth: BLOCK_COLUMN_WIDTH,
        maxWidth: BLOCK_COLUMN_WIDTH,
        cellRendererSelector: (params: ICellRendererParams<DspInventoryStats, string>) => {
          if (params.node.rowPinned) return undefined;
          const btns: ActionButton[] = [
            {
              text: this.translocoService.translate("dsp-blacklist.block"),
              tooltip: this.isBlacklistFull()
                ? this.translocoService.translate("dsp-blacklist.limit_reached", { max: MAX_BLACKLIST_ENTRIES })
                : undefined,
              disabled: isReadOnly || this.isBlacklistFull(),
              onClick: () => this.blacklist(params.data!),
            },
          ];

          return {
            component: ActionButtonsComponent,
            params: {
              actionButtons: btns,
            },
          };
        },
      },
    ];
  });

  saveChanges() {
    if (this.isReadOnly()) return;
    const { added, removed } = this.blacklistDiff();
    const accountMarketplace = this.accountMarketplace();

    this.isLoading.set(true);

    concat(
      removed.length > 0 ? this.dspService.unblacklistDomains(accountMarketplace, removed) : EMPTY,
      added.length > 0 ? this.dspService.blacklistDomains(accountMarketplace, added) : EMPTY,
    )
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.reloadBlacklist.next();
        }),
      )
      .subscribe();
  }

  resetChanges() {
    this.draftBlacklist.set(this.blacklistSource());
  }

  openUnsavedChangesWarningModal() {
    return this.modalService
      .openModal<void, boolean>(Dashboard360UsavedChangesModalComponent, {
        modalTitle: this.translocoService.translate("dashboard360.unsaved_changes_modal_title"),
      })
      .afterClosed()
      .pipe(
        take(1),
        tap((result) => {
          if (result && !this.isReadOnly()) {
            this.saveChanges();
          } else {
            this.resetChanges();
          }
        }),
        map(() => true),
      );
  }

  private blacklistTextColumn(
    headerKey: string,
    field: keyof Pick<DspBlacklist, "description" | "domainOrAppId">,
  ): ColDef<DspBlacklist> {
    return {
      headerName: this.translocoService.translate(headerKey),
      headerValueGetter: () => this.translocoService.translate(headerKey),
      field,
      sort: field === "description" ? "asc" : undefined,
      floatingFilter: true,
      flex: 1,
      filter: "agTextColumnFilter",
    };
  }

  // A single site often costs less than one unit of currency: whole-unit rounding would show 0.
  private costWithCentsColumn(col: ColDef<DspInventoryStats>): ColDef<DspInventoryStats> {
    return {
      ...col,
      sort: "desc",
      cellRenderer: undefined,
      cellRendererParams: undefined,
      valueFormatter: (params: ValueFormatterParams<DspInventoryStats, number>) =>
        DSP_COST.format(params.value ?? 0, this.locale(), this.currency(), "1.2-2"),
    };
  }

  private filterDataByInventoryType(
    stats: DspInventoryStats[],
    inventoryType: DspBlacklistTypeEnum,
  ): DspInventoryStats[] {
    if (inventoryType === DspBlacklistTypeEnum.APP) {
      return stats.filter((stat) => isApp(stat.site ?? ""));
    }
    return stats.filter((stat) => !isApp(stat.site ?? ""));
  }

  private blacklist(stat: DspInventoryStats) {
    if (this.isReadOnly() || this.isBlacklistFull()) {
      return;
    }
    const entry = getDspBlacklistFromSite(stat.site ?? "");
    const key = dspBlacklistKey(entry);
    this.draftBlacklist.update((list) => (list.some((d) => dspBlacklistKey(d) === key) ? list : [...list, entry]));
  }

  private whitelist(entry: DspBlacklist) {
    if (this.isReadOnly()) return;
    const key = dspBlacklistKey(entry);
    this.draftBlacklist.update((list) => list.filter((d) => dspBlacklistKey(d) !== key));
  }

  private buildRowData<
    T extends DspStats & {
      stats?: DspStats[];
    },
  >(stats: DspStats[], keyFn: (stat: DspStats) => string, init: (stat: DspStats) => T): T[] {
    if (!stats || stats.length === 0) {
      return [];
    }
    const strategyStats: Map<string, T> = new Map();
    for (const stat of stats) {
      const key = keyFn(stat);
      if (!strategyStats.has(key)) {
        strategyStats.set(key, {
          ...init(stat),
          stats: [stat],
        });
      } else {
        const existing = strategyStats.get(key);
        strategyStats.set(key, { ...existing!, ...sumDspStats(existing!, stat), stats: [...existing!.stats!, stat] });
      }
    }
    return Array.from(strategyStats.values());
  }
}
